import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, type User } from "firebase/auth";
import { auth } from "../firebase";
import { authApi } from "../api/authApi"
import type { CurrentAuthUser } from "../types/auth.types"

interface AuthContextValue {
  firebaseUser: User | null;
  session: CurrentAuthUser | null;
  loading: boolean;

  login: (
    email: string,
    password: string,
  ) => Promise<void>;

  logout: () => Promise<void>;

  refreshSession: () => Promise<void>;

  hasPermission: (
    permission: string,
  ) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [session, setSession] = useState<CurrentAuthUser | null>(null);
  const [loading, setLoading] = useState(true);

const loadSession = useCallback(async () => {
  if (!auth.currentUser) {
    setSession(null);
    return;
  }

  const currentSession = await authApi.getMe();

  if (!currentSession.dashboard) {
    throw new Error(
      "DASHBOARD_ACCOUNT_NOT_FOUND",
    );
  }

  if (!currentSession.dashboard.isActive) {
    throw new Error(
      "DASHBOARD_ACCOUNT_INACTIVE",
    );
  }

  setSession(currentSession);
}, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setFirebaseUser(user);

      try {
        if (user) {
          await loadSession();
        } else {
          setSession(null);
        }
      } catch (error) {
        console.error("Failed to load dashboard session:", error);
        setSession(null);
        await signOut(auth);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [loadSession]);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      await loadSession();
    } finally {
      setLoading(false);
    }
  }, [loadSession]);

  const logout = useCallback(async () => {
    await signOut(auth);
    setSession(null);
    localStorage.removeItem("dashboardUser");
  }, []);

  const hasPermission = useCallback(
  (permission: string): boolean => {
    const dashboard = session?.dashboard;

    if (!dashboard) {
      return false;
    }

    if (dashboard.isAdmin) {
      return true;
    }

    return dashboard.permissions.includes(
      permission,
    );
  },
  [session],
);

const value = useMemo<AuthContextValue>(
  () => ({
    firebaseUser,
    session,
    loading,
    login,
    logout,
    refreshSession: loadSession,
    hasPermission,
  }),
  [
    firebaseUser,
    session,
    loading,
    login,
    logout,
    loadSession,
    hasPermission,
  ],
);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
